import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "~/components/ui/dialog";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { useCreateTotem, useGenerateTotemCode } from "../hooks/useTotems";
import { formatTotemNumber } from "../utils/format";

interface AddTotemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddTotemDialog({ open, onOpenChange }: AddTotemDialogProps) {
  const [code, setCode] = useState("");
  const [capacityLiters, setCapacityLiters] = useState(1000);

  const { data: suggestedCode, refetch: refetchCode } = useGenerateTotemCode();
  const createTotem = useCreateTotem();

  useEffect(() => {
    if (open && suggestedCode) {
      setCode(suggestedCode);
    }
  }, [open, suggestedCode]);

  useEffect(() => {
    if (open) {
      refetchCode();
    }
  }, [open, refetchCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!code.trim()) return;

    try {
      await createTotem.mutateAsync({
        code: code.trim(),
        capacity_liters: capacityLiters,
        status: "available",
      });
      onOpenChange(false);
      setCode("");
      setCapacityLiters(1000);
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Agregar Nuevo Totem</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Número</Label>
              <div className="flex h-9 items-center rounded-md border bg-muted/40 px-3 text-sm font-medium">
                {code ? formatTotemNumber(code) : "—"}
              </div>
              <p className="text-xs text-muted-foreground">
                Número asignado automáticamente
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="capacity">Capacidad (Litros)</Label>
              <Input
                id="capacity"
                type="number"
                value={capacityLiters}
                onChange={(e) => setCapacityLiters(Number(e.target.value))}
                min={100}
                max={2000}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={createTotem.isPending}>
              {createTotem.isPending ? "Creando..." : "Crear Totem"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
